import { Album, Credential, ServiceProvider } from '@uploadcam/common';
import { defineFeature, FeatureContext } from './features/shared/services/features.service';

const DBX_CONTENT_WRITE = 'files.content.write';
const GOOGLE_DRIVE_FILE_FULL_ACCESS = 'https://www.googleapis.com/auth/drive';
const ONEDRIVE_FILE_READWRITE = 'files.readwrite';
const ONEDRIVE_FILE_READWRITE_ALL = 'files.readwrite.all'; // TODO: Remove this scope eventually as it was added only for backward compatibility for users that had already granted this scope before we requested the 'files.readwrite' scope only
const ONEDRIVE_SITES_READWRITE_ALL = 'sites.readwrite.all';

function hasScopeOrUpgrade(credential: Credential, scope: string): boolean | string[] {
  if (credential.scopes.includes(scope)) {
    return true;
  } else {
    return [scope];
  }
}

export function canAssignFolders() {
  return defineFeature(
    ({ env, credential }) =>
      env === 'development' || credential?.provider !== ServiceProvider.Microsoft,
  );
}

export function canDeleteFeedItems() {
  return defineFeature(({ credential, flavor }) => {
    if (flavor === 'sc') {
      return false;
    }

    // Google Drive
    if (credential?.provider === ServiceProvider.Google) {
      return hasScopeOrUpgrade(credential, GOOGLE_DRIVE_FILE_FULL_ACCESS);
    }

    // Dropbox
    if (credential?.provider === ServiceProvider.Dropbox) {
      return credential.scopes.includes(DBX_CONTENT_WRITE);
    }

    // OneDrive
    if (credential?.provider === ServiceProvider.Microsoft) {
      return (
        credential.scopes.includes(ONEDRIVE_FILE_READWRITE) ||
        credential.scopes.includes(ONEDRIVE_FILE_READWRITE_ALL) ||
        credential.scopes.includes(ONEDRIVE_SITES_READWRITE_ALL)
      );
    }

    return false;
  });
}

export function canManageTeamTags() {
  // Enabled for all flavors now that the mobile app supports tags
  return defineFeature(({ user }) => (user?.isAdmin || user?.isFeedManager) ?? false);
}

export function canManageTeamEmailNotificationsSettings() {
  return defineFeature(({ env, flavor }) => {
    // SchoolCam doesn't support Deep Links yet, so only for UploadCam for now
    return flavor === 'uc';
  });
}

export function canManageNotifications() {
  return defineFeature(({ env, flavor }) => {
    // We will keep notifications an UploadCam feature only
    return flavor === 'uc';
  });
}

export function canSubscribeToAllAlbumNotifications() {
  return defineFeature(({ user }) => {
    return user?.isAdmin ?? false;
  });
}

export function canGenerateReports() {
  return defineFeature(({ env, flavor, team }) => {
    if (flavor === 'uc') {
      return true;
    }

    return false;
  });
}

export function canCreateAPIKeys() {
  return defineFeature(({ env, flavor, team }) => {
    if (flavor === 'sc') {
      return false;
    }

    if (env === 'development') {
      return true;
    }

    const allowedTeams = [
      '0uFtxcFnsgDarapkuYNi', // Bitwise Microsoft Team Production
      'faXqVqCiRIOq3sYRpQO5', // Bitwise Staging Team
      'DCjluBR3yT67AWPd3Pky', // Florida Home 360
      'lOBu9pHOIpgyMHQbXrjM', // Bitwise Google Team Production
      '5whwBt7gAd2yGnq2Vv8C', // M25 Movers
    ];

    return allowedTeams.includes(team?.id ?? '');
  });
}

export function canMoveFeedItems() {
  return defineFeature(({ flavor, team, credential }) => {
    if (flavor === 'sc') {
      return false;
    }

    const allowedTeams = [
      '0uFtxcFnsgDarapkuYNi', // Bitwise Microsoft Team Production
      'iOjzlZ5EzesWMKwViiqz', // Bitwise Staging Team
      'lOBu9pHOIpgyMHQbXrjM', // Bitwise Google Production
      'wnAFOVCvHNlijg83R89X', // Chase Communications - (Chase Ducote)
    ];

    // Google Drive
    if (credential?.provider === ServiceProvider.Google) {
      return hasScopeOrUpgrade(credential, GOOGLE_DRIVE_FILE_FULL_ACCESS);
    }

    // Dropbox
    if (credential?.provider === ServiceProvider.Dropbox) {
      return credential.scopes.includes(DBX_CONTENT_WRITE);
    }

    // OneDrive
    if (
      allowedTeams.includes(team?.id ?? '') &&
      credential?.provider === ServiceProvider.Microsoft
    ) {
      return (
        credential.scopes.includes(ONEDRIVE_FILE_READWRITE) ||
        credential.scopes.includes(ONEDRIVE_FILE_READWRITE_ALL) ||
        credential.scopes.includes(ONEDRIVE_SITES_READWRITE_ALL)
      );
    }

    // Zoho
    if (credential?.provider === ServiceProvider.Zoho) {
      // We should potentially check for specific Zoho scopes here when we have them
      return true;
    }

    return false;
  });
}

export function canEditAlbumFn(ctx: FeatureContext & { album?: Album }) {
  const { album, credential, flavor, user } = ctx;

  if (!user) {
    return false;
  }

  // If the user is the Album creator, they can edit it
  if (album && user.id === album.createdBy) {
    return true;
  }

  // In SchoolCam we ignore any other condition (e.g. scopes). Legacy Albums
  // created before we started saving `createdBy` have no creator, so for those
  // we allow admins to edit (which is what the backend allows anyway).
  if (flavor === 'sc') {
    return !!album && !album.createdBy && user.isAdmin;
  }

  // For UploadCam + Google Drive, require full access
  if (credential?.provider === ServiceProvider.Google) {
    return hasScopeOrUpgrade(credential, GOOGLE_DRIVE_FILE_FULL_ACCESS);
  }

  return true;
}

export function canBrowseAlbum() {
  return defineFeature(({ credential, flavor }) => {
    if (flavor === 'sc') {
      return false;
    }

    if (credential?.provider === ServiceProvider.Google) {
      return hasScopeOrUpgrade(credential, GOOGLE_DRIVE_FILE_FULL_ACCESS);
    }

    // TODO: Use also the scopes instead of just the flavor
    return true;
  });
}

export function canPickExistingFolder() {
  return defineFeature(({ credential, flavor }) => {
    if (flavor === 'sc') {
      return false;
    }

    if (credential?.provider === ServiceProvider.Google) {
      return hasScopeOrUpgrade(credential, GOOGLE_DRIVE_FILE_FULL_ACCESS);
    }

    return true;
  });
}

export function isChangeRootRecreateUserDefaultAlbumsEnabled() {
  return defineFeature(({ env, team }) => {
    const allowedTeamIds = [
      'Ih40TrRw3Pw8hOgEpr9k', // Bitwise School (Staging)
      'SVA9tXNmkD7G45JtaghL', // Bitwise Demo School (Production)
      'TkHAeLJL6jV6S3zTLxxu', // The KAUST School
      'J4EyCDJkSb5GwuNkzyIB', // stmarkhouston.org
    ];

    if (team && allowedTeamIds.includes(team.id)) {
      return true;
    }

    return env === 'development';
  });
}

export function isDiagnosticsEnabled() {
  return defineFeature(({ env, credential, team }) => {
    if (env === 'development' || env === 'staging') {
      return true;
    }

    if (credential?.provider === ServiceProvider.Google) {
      const allowedTeamIds = [
        'Ih40TrRw3Pw8hOgEpr9k', // Bitwise School (Staging)
        'SVA9tXNmkD7G45JtaghL', // Bitwise Demo School (Production)
        'X7ZxQalH3sKkhe9PfLPG', // fis.edu - Frankfurt school(Production)
        'xhDnT0WEvsFDWXb6OBLs', // nist.ac.th - Thailand school(Production)
        'pjgeVn8VUa3BAOLSQpqp', // American Landscape (Production)
      ]; // Add your team IDs here
      if (team?.id && allowedTeamIds.includes(team.id)) {
        return true;
      }
    }

    return false;
  });
}

export function isMicrosoftTeamSitesAuthEnabled() {
  return defineFeature(({ env }) => env === 'development');
}

export function canDisplayMapInGallery() {
  return defineFeature(({ flavor }) => flavor === 'uc');
}

export function galleriesEnabled() {
  return defineFeature(({ env, team, credential }) => {
    // Galleries mirror file bytes server-side, which Zoho's client-based OAuth app
    // can't support (no server-side credentials; server-based app blocks browser CORS).
    // See docs/zoho-authentication.md and mirror-feed-item.fn.ts.
    if (credential?.provider === ServiceProvider.Zoho) {
      return false;
    }

    // Always on in dev + staging so we can keep iterating without managing a list.
    if (env === 'development' || env === 'staging') {
      return true;
    }

    // Production beta — add team ids here as we onboard pilots.
    const allowedTeams: string[] = [
      'lOBu9pHOIpgyMHQbXrjM', // Bitwise Google Team Production
      '0uFtxcFnsgDarapkuYNi', // Bitwise Microsoft Team Production
      'PAscbT1DSpJWWD6AV4Ua', // Mulgrave School (Production)
      'SVA9tXNmkD7G45JtaghL', // Bitwise Demo School (Production)
      'eo8Y1giMowe9M4l6E8s4', // Zoran Team (Production)
    ];

    return allowedTeams.includes(team?.id ?? '');
  });
}

export function canBulkImportFolders() {
  return defineFeature(({ env, flavor, team }) => {
    // Bulk folder import from CSV is an UploadCam-only feature for now.
    if (flavor !== 'uc') {
      return false;
    }

    // Always on in dev + staging so we can keep iterating without managing a list.
    if (env === 'development' || env === 'staging') {
      return true;
    }

    // Production pilot — add team ids here as we onboard testers.
    const allowedTeams: string[] = [
      'lOBu9pHOIpgyMHQbXrjM', // Bitwise Google Team Production
      'W8ypnhrivlzsixQSvtW1', // trandp.com.au (Production)
      'UZvejfxcew7B3m8p5nxj', // jeremy@whitepacific.ca (Production)
      '5whwBt7gAd2yGnq2Vv8C', // M25 Movers (Production) - Avelino
      '8vkJKj8NQea18wvE3evv', // M25 Movers (Zoho) - Avelino
    ];

    return allowedTeams.includes(team?.id ?? '');
  });
}
